import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(customParseFormat);
dayjs.extend(localizedFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.Ls.en.invalidDate = 'Invalid date';

let defaultTimezone;

const wallTimeInZone = (wall, zone) => {
    const text = wall.format('YYYY-MM-DD HH:mm:ss.SSS');
    const result = dayjs.tz(text, zone);
    // Choose the earlier occurrence of an ambiguous fall-back wall time, as before.
    const previousOffset = dayjs(wall.valueOf() - 86400000).tz(zone).utcOffset();
    const earlier = dayjs(wall.valueOf() - previousOffset * 60000).tz(zone);
    return earlier.valueOf() < result.valueOf() && earlier.format('YYYY-MM-DD HH:mm:ss.SSS') === text
        ? earlier : result;
};

const parse = (value, format, strict, useUTC) => {
    const factory = useUTC ? dayjs.utc : dayjs;
    if (!format) return factory(value);
    const formats = Array.isArray(format) ? format : [format];
    for (const candidate of formats) {
        const result = factory(value, candidate, strict);
        if (result.isValid()) return result;
    }
    return factory(null);
};

// Calendar arithmetic must refresh the IANA offset when it crosses a DST boundary.
// Day.js otherwise retains the source offset after add/subtract.
dayjs.extend((_, Dayjs) => {
    const startOf = Dayjs.prototype.startOf;
    Dayjs.prototype.startOf = function (unit, start) {
        const zone = this.$x?.$timezone;
        if (!zone || !this.isValid()) return startOf.call(this, unit, start);
        const wall = dayjs.utc(this.format('YYYY-MM-DD HH:mm:ss.SSS'));
        return wallTimeInZone(wall.startOf(unit, start), zone);
    };
    const add = Dayjs.prototype.add;
    Dayjs.prototype.add = function (amount, unit) {
        const result = add.call(this, amount, unit);
        const zone = this.$x?.$timezone;
        return zone && /^(day|days|d|week|weeks|w|month|months|M|year|years|y)$/.test(unit)
            ? wallTimeInZone(dayjs.utc(result.format('YYYY-MM-DD HH:mm:ss.SSS')), zone)
            : result;
    };
});

// Match the application's former default-zone factory: strings without offsets
// are wall times; Date, numeric, and offset-bearing inputs represent instants.
const appDayjs = (value, format, strict) => {
    if (dayjs.isDayjs(value)) return value.clone();
    // Reject overflowing ISO calendar fields instead of rolling into another month.
    if (typeof value === 'string' && !format) {
        const isoDate = /^(\d{4}-\d{2}-\d{2})(?:[T ]|$)/.exec(value);
        if (isoDate && !dayjs.utc(isoDate[1], 'YYYY-MM-DD', true).isValid()) return dayjs(null);
    }
    // Preserve native-Date fallback for legacy unformatted slash dates.
    if (typeof value === 'string' && !format && value.includes('/')) {
        const result = dayjs(new Date(value));
        return defaultTimezone ? result.tz(defaultTimezone) : result;
    }
    if (!defaultTimezone) return parse(value, format, strict, false);
    if (typeof value === 'string' && !/(?:Z|[+-]\d\d:?\d\d)$/i.test(value)) {
        const parsed = parse(value, format, strict, true);
        return parsed.isValid()
            ? wallTimeInZone(parsed, defaultTimezone)
            : parsed;
    }
    return parse(value, format, strict, false).tz(defaultTimezone);
};

appDayjs.isDayjs = dayjs.isDayjs;
appDayjs.tz = {
    setDefault: zone => { defaultTimezone = zone; },
};

export default appDayjs;
